import { ConfirmDialog } from '@easymusic/ui';
import { useState, type ComponentProps, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/** A question asked before something a person may regret. */
export interface Question {
  /** The question itself: "Remove Anna from your students?" */
  title: string;
  /** What follows from saying yes, when the question does not say it. */
  text?: ReactNode;
  /** The action by its own name: "Remove", "Leave without saving". */
  confirmLabel: string;
  /** It cannot be taken back. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

/**
 * Ask before acting. Returns the way to ask and the dialog to render, somewhere in
 * the component that asks:
 *
 *   const [ask, dialog] = useConfirm();
 *   <Button onClick={() => ask({ title, confirmLabel, onConfirm: remove })}>…</Button>
 *   {dialog}
 *
 * One question at a time: asking again replaces the one on screen.
 */
export function useConfirm(): [ask: (question: Question) => void, dialog: ReactNode] {
  const { t } = useTranslation('common');
  const [question, setQuestion] = useState<Question | null>(null);
  const answer = (act?: () => void) => {
    setQuestion(null);
    act?.();
  };
  const dialog = (
    <ConfirmDialog
      open={question !== null}
      title={question?.title ?? ''}
      confirmLabel={question?.confirmLabel ?? ''}
      cancelLabel={t('dialog.cancel')}
      destructive={question?.destructive}
      onConfirm={() => answer(question?.onConfirm)}
      onCancel={() => answer(question?.onCancel)}
    >
      {question?.text}
    </ConfirmDialog>
  );
  return [setQuestion, dialog];
}

/**
 * The same dialog for a question that needs an answer typed in (a password, a code):
 * the fields go in `children`, and the one asking decides when it closes.
 */
export function AskDialog(props: ComponentProps<typeof ConfirmDialog>) {
  return <ConfirmDialog {...props} />;
}
